import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import Avatar from '../components/Avatar';
import { 
  Search, 
  Users, 
  Plus, 
  Phone, 
  Mail, 
  Building, 
  Star, 
  FolderPlus, 
  Trash2, 
  Edit3, 
  Send, 
  CheckSquare, 
  Square,
  Lock,
  Globe,
  X,
  PhoneCall,
  LayoutGrid,
  List,
  Copy,
  Check
} from 'lucide-react';

const DEFAULT_TABS = [
  { id: 'INTERNAL', name: '사내 임직원', type: 'system' },
  { id: 'ALL_CUSTOMERS', name: '전체 고객/거래처', type: 'system' },
  { id: 'PRIVATE', name: '🔒 나만 보는 연락처', type: 'system' },
  { id: 'VIP', name: '중요 고객', type: 'system' },
  { id: 'GENERAL', name: '기타 고객', type: 'system' }
];

const isRetiredEmployee = (u) => {
  if (!u) return true;
  if (u.UserID === 'system-config') return true;
  const status = (u.Status || u.status || '').toUpperCase();
  if (status === 'RETIRED' || status === 'RESIGNED' || status === 'INACTIVE' || u.IsRetired === true || u.Resigned === true || u.IsResigned === true) {
    return true;
  }
  return false;
};

const AddressBookTab = ({ onComposeTo }) => {
  const { users, currentUser, customers, addCustomer, updateCustomer, deleteCustomer } = useContext(AppContext);
  
  // Custom Tabs state
  const [tabs, setTabs] = useState(() => {
    try {
      const saved = localStorage.getItem('clou_addressbook_custom_tabs_v3');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn('Failed to load custom addressbook tabs:', e);
    }
    return DEFAULT_TABS;
  });

  const [activeTabId, setActiveTabId] = useState('INTERNAL');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  
  // Modal states
  const [detailModalContact, setDetailModalContact] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Multi-select state for bulk mail send
  const [selectedEmailSet, setSelectedEmailSet] = useState(new Set());
  const [copiedField, setCopiedField] = useState(null);

  // Form State
  const [custCategory, setCustCategory] = useState('VIP');
  const [custVisibility, setCustVisibility] = useState('PUBLIC'); // 'PUBLIC' | 'PRIVATE'
  const [custName, setCustName] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custCompany, setCustCompany] = useState('');
  const [custMemo, setCustMemo] = useState('');

  // Persist custom tabs
  const saveCustomTabs = (newTabs) => {
    setTabs(newTabs);
    localStorage.setItem('clou_addressbook_custom_tabs_v3', JSON.stringify(newTabs));
  };

  const handleAddCustomTab = () => {
    const tabName = prompt('추가할 새 주소록 카테고리 이름을 입력하세요 (예: 협력사, 외주 파트너):');
    if (!tabName || !tabName.trim()) return;
    const newTabId = `CUSTOM_${Date.now()}`;
    const updated = [...tabs, { id: newTabId, name: tabName.trim(), type: 'custom' }];
    saveCustomTabs(updated);
    setActiveTabId(newTabId);
  };

  const handleComposeClick = (email) => {
    if (onComposeTo) {
      onComposeTo(email);
    }
    window.dispatchEvent(new CustomEvent('trigger-compose', {
      detail: { to: email }
    }));
  };

  const handleBulkComposeClick = () => {
    const emailList = Array.from(selectedEmailSet).join(', ');
    if (!emailList) {
      alert('메일을 발송할 대상을 선택해 주세요.');
      return;
    }
    handleComposeClick(emailList);
  };

  const toggleSelectEmail = (email) => {
    const next = new Set(selectedEmailSet);
    if (next.has(email)) {
      next.delete(email);
    } else {
      next.add(email);
    }
    setSelectedEmailSet(next);
  };

  const handleCopyText = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Open add customer modal
  const handleOpenAddModal = (defaultCategory = 'VIP', defaultVisibility = 'PUBLIC') => {
    setEditingCustomer(null);
    setCustCategory(defaultCategory === 'INTERNAL' ? 'VIP' : defaultCategory);
    setCustVisibility(defaultVisibility);
    setCustName('');
    setCustEmail('');
    setCustPhone('');
    setCustCompany('');
    setCustMemo('');
    setIsFormModalOpen(true);
  };

  // Open edit customer modal
  const handleOpenEditModal = (customer) => {
    setEditingCustomer(customer);
    setCustCategory(customer.Category || 'VIP');
    setCustVisibility(customer.Visibility || 'PUBLIC');
    setCustName(customer.Name || '');
    setCustEmail(customer.Email || '');
    setCustPhone(customer.Phone || '');
    setCustCompany(customer.Company || '');
    setCustMemo(customer.Memo || '');
    setIsFormModalOpen(true);
    setDetailModalContact(null);
  };

  // Listen to Customer Add event from sidebar or other tabs
  useEffect(() => {
    const handleTriggerAdd = () => {
      handleOpenAddModal('VIP', 'PUBLIC');
    };
    window.addEventListener('trigger-add-customer', handleTriggerAdd);
    return () => {
      window.removeEventListener('trigger-add-customer', handleTriggerAdd);
    };
  }, []);

  // Check if private contact is visible to current logged-in user
  const isContactVisibleToMe = useCallback((c) => {
    if (c.Visibility === 'PRIVATE') {
      const myId = currentUser?.UserID;
      const myEmail = (currentUser?.Email || '').toLowerCase();
      return (c.OwnerID && c.OwnerID === myId) || 
             (c.OwnerID && c.OwnerID.toLowerCase() === myEmail) || 
             (c.CreatedBy && c.CreatedBy === currentUser?.Name) ||
             (!c.OwnerID && !c.CreatedBy);
    }
    return true; // PUBLIC contacts are visible to all employees
  }, [currentUser]);

  // Active (Non-retired) Employees
  const activeEmployees = useMemo(() => {
    return (users || [])
      .filter(u => !isRetiredEmployee(u))
      .map(u => ({ ...u, _isInternal: true }));
  }, [users]);

  // Filtered contacts based on active tab and privacy scope
  const tabFilteredList = useMemo(() => {
    if (activeTabId === 'INTERNAL') {
      return activeEmployees;
    }

    const visibleCustomers = (customers || []).filter(isContactVisibleToMe);

    if (activeTabId === 'ALL_CUSTOMERS') {
      return visibleCustomers.map(c => ({ ...c, _isInternal: false }));
    }
    if (activeTabId === 'PRIVATE') {
      return visibleCustomers.filter(c => c.Visibility === 'PRIVATE').map(c => ({ ...c, _isInternal: false }));
    }
    if (activeTabId === 'VIP') {
      return visibleCustomers.filter(c => c.Category === 'VIP' || !c.Category).map(c => ({ ...c, _isInternal: false }));
    }
    if (activeTabId === 'GENERAL') {
      return visibleCustomers.filter(c => c.Category === 'GENERAL').map(c => ({ ...c, _isInternal: false }));
    }
    return visibleCustomers.filter(c => c.Category === activeTabId).map(c => ({ ...c, _isInternal: false }));
  }, [activeTabId, activeEmployees, customers, isContactVisibleToMe]);

  // Filter by search keyword
  const filteredList = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return tabFilteredList;
    return tabFilteredList.filter(item => {
      return (
        (item.Name || '').toLowerCase().includes(q) ||
        (item.Email || '').toLowerCase().includes(q) ||
        (item.Company || item.Department || '').toLowerCase().includes(q) ||
        (item.JobTitle || item.Position || '').toLowerCase().includes(q) ||
        (item.Phone || '').includes(q) ||
        (item.Memo || '').toLowerCase().includes(q)
      );
    });
  }, [tabFilteredList, searchTerm]);

  // Counts for each tab
  const tabCounts = useMemo(() => {
    const visibleCustomers = (customers || []).filter(isContactVisibleToMe);
    const map = {
      INTERNAL: activeEmployees.length,
      ALL_CUSTOMERS: visibleCustomers.length,
      PRIVATE: visibleCustomers.filter(c => c.Visibility === 'PRIVATE').length,
      VIP: visibleCustomers.filter(c => c.Category === 'VIP' || !c.Category).length,
      GENERAL: visibleCustomers.filter(c => c.Category === 'GENERAL').length
    };
    tabs.forEach(t => {
      if (!map[t.id]) {
        map[t.id] = visibleCustomers.filter(c => c.Category === t.id).length;
      }
    });
    return map;
  }, [activeEmployees, customers, tabs, isContactVisibleToMe]);

  // Submit Save Customer
  const handleSaveCustomerSubmit = (e) => {
    e.preventDefault();
    if (!custName.trim() || !custEmail.trim()) {
      alert('고객 이름과 이메일은 필수 입력 항목입니다.');
      return;
    }

    if (editingCustomer) {
      const updated = {
        ...editingCustomer,
        Name: custName.trim(),
        Email: custEmail.trim(),
        Phone: custPhone.trim(),
        Company: custCompany.trim(),
        Category: custCategory,
        Visibility: custVisibility,
        Memo: custMemo.trim()
      };
      if (typeof updateCustomer === 'function') {
        updateCustomer(updated);
      }
      setIsFormModalOpen(false);
      setEditingCustomer(null);
      alert('연락처 정보가 수정되었습니다.');
    } else {
      addCustomer({
        Name: custName.trim(),
        Email: custEmail.trim(),
        Phone: custPhone.trim(),
        Company: custCompany.trim(),
        Category: custCategory || 'VIP',
        Visibility: custVisibility,
        Memo: custMemo.trim(),
        OwnerID: currentUser?.UserID || currentUser?.Email,
        CreatedBy: currentUser?.Name || '본인'
      });
      setIsFormModalOpen(false);
      alert(custVisibility === 'PRIVATE' ? '나만 보는 비공개 연락처로 등록되었습니다.' : '공유 주소록에 새 고객 정보가 등록되었습니다.');
    }
  };

  const handleDeleteCustomer = (customer) => {
    if (window.confirm(`'${customer.Name}' 연락처를 주소록에서 삭제하시겠습니까?`)) {
      if (typeof deleteCustomer === 'function') {
        deleteCustomer(customer.CustomerID);
      }
      setDetailModalContact(null);
      alert('연락처가 삭제되었습니다.');
    }
  };

  const handleSelectAllInView = () => {
    if (selectedEmailSet.size === filteredList.length && filteredList.length > 0) {
      setSelectedEmailSet(new Set());
    } else {
      const next = new Set(filteredList.map(item => item.Email).filter(Boolean));
      setSelectedEmailSet(next);
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 bg-slate-50 min-h-[calc(100vh-80px)] text-slate-800 space-y-6 max-w-7xl mx-auto">
      
      {/* 1. Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-1 text-left">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center font-bold shadow-md shadow-indigo-100">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">주소록 & 고객 관리</h1>
              <p className="text-xs md:text-sm text-slate-500 mt-0.5">
                사내 임직원 연락망, 전사 공유 고객사, 나만 보는 개인 비공개 연락처를 한곳에서 관리합니다.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto flex-wrap">
          {selectedEmailSet.size > 0 && (
            <button
              onClick={handleBulkComposeClick}
              className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-2 cursor-pointer transition-all active:scale-95"
            >
              <Send className="w-4 h-4" />
              <span>선택 {selectedEmailSet.size}명에게 메일 발송</span>
            </button>
          )}

          <button
            onClick={() => handleOpenAddModal(activeTabId === 'INTERNAL' ? 'VIP' : activeTabId, activeTabId === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC')}
            className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer transition-all active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>새 연락처 등록</span>
          </button>
        </div>
      </div>

      {/* 2. Top Filter & Search Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-3.5">
        
        {/* Category Tabs */}
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-1 scrollbar-none">
          <div className="flex items-center gap-1.5 flex-nowrap">
            {tabs.map(tab => {
              const isActive = activeTabId === tab.id;
              const count = tabCounts[tab.id] || 0;

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTabId(tab.id);
                    setSelectedEmailSet(new Set());
                  }}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                    isActive 
                      ? 'bg-indigo-600 text-white shadow-sm font-black' 
                      : 'bg-slate-50 border border-slate-200/80 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {tab.id === 'VIP' && <Star className={`w-3.5 h-3.5 ${isActive ? 'text-amber-300 fill-amber-300' : 'text-amber-500 fill-amber-500'}`} />}
                  {tab.id === 'PRIVATE' && <Lock className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-200' : 'text-slate-500'}`} />}
                  <span>{tab.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                    isActive ? 'bg-indigo-700/80 text-indigo-100' : 'bg-slate-200 text-slate-700'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}

            <button
              onClick={handleAddCustomTab}
              className="p-2 text-indigo-600 hover:bg-indigo-50 border border-dashed border-indigo-200 rounded-xl text-xs font-bold transition-all cursor-pointer flex-shrink-0 flex items-center gap-1"
              title="새 그룹/카테고리 추가"
            >
              <FolderPlus className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">+그룹 추가</span>
            </button>
          </div>

          {/* View Mode Toggle (Grid vs List) */}
          <div className="hidden sm:flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 flex-shrink-0">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-400 hover:text-slate-700'}`}
              title="카드 그리드 뷰"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded-lg transition-all cursor-pointer ${viewMode === 'list' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-400 hover:text-slate-700'}`}
              title="목록 테이블 뷰"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search and Selection Toolbar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1 border-t border-slate-100">
          <div className="relative w-full sm:w-96">
            <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="이름, 이메일, 회사/부서, 연락처 검색..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-indigo-600 focus:bg-white text-slate-900 placeholder-slate-400 transition-all font-medium"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-xs font-bold"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center justify-between w-full sm:w-auto gap-3 text-xs text-slate-500">
            <button
              type="button"
              onClick={handleSelectAllInView}
              className="flex items-center gap-1.5 text-slate-700 hover:text-indigo-600 font-bold cursor-pointer transition-colors"
            >
              {selectedEmailSet.size === filteredList.length && filteredList.length > 0 ? (
                <CheckSquare className="w-4 h-4 text-indigo-600" />
              ) : (
                <Square className="w-4 h-4 text-slate-400" />
              )}
              <span>전체 선택 ({filteredList.length}건)</span>
            </button>
          </div>
        </div>
      </div>

      {/* 3. Full-width Main Contact Directory (Grid or List View) */}
      {filteredList.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center shadow-xs space-y-3">
          <div className="w-14 h-14 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto text-2xl">
            👥
          </div>
          <h3 className="text-base font-bold text-slate-700">조회된 연락처가 없습니다</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            {searchTerm ? `'${searchTerm}' 검색어와 일치하는 연락처가 없습니다.` : '해당 카테고리에 등록된 연락처가 없습니다. 새로운 연락처를 등록해 보세요.'}
          </p>
          {activeTabId !== 'INTERNAL' && (
            <button
              onClick={() => handleOpenAddModal(activeTabId, activeTabId === 'PRIVATE' ? 'PRIVATE' : 'PUBLIC')}
              className="mt-2 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-bold text-xs rounded-xl border border-indigo-200 inline-flex items-center gap-1 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>연락처 추가하기</span>
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        /* A. Card Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredList.map(item => {
            const itemKey = item.UserID || item.CustomerID;
            const isChecked = selectedEmailSet.has(item.Email);
            const isPrivate = item.Visibility === 'PRIVATE';

            return (
              <div
                key={itemKey}
                onClick={() => setDetailModalContact(item)}
                className="bg-white border border-slate-200 hover:border-indigo-300 rounded-2xl p-4 shadow-xs hover:shadow-md transition-all text-left flex flex-col justify-between space-y-3.5 group cursor-pointer relative"
              >
                {/* Card Top: Checkbox & Badges */}
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelectEmail(item.Email);
                    }}
                    className="p-1 -ml-1 text-slate-400 hover:text-indigo-600 cursor-pointer"
                  >
                    {isChecked ? (
                      <CheckSquare className="w-4 h-4 text-indigo-600" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>

                  <div className="flex items-center gap-1 flex-wrap justify-end">
                    {item._isInternal ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                        🏢 사내직원
                      </span>
                    ) : isPrivate ? (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" />
                        <span>나만보기</span>
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 flex items-center gap-1">
                        <Globe className="w-2.5 h-2.5" />
                        <span>전사공유</span>
                      </span>
                    )}

                    {item.Category === 'VIP' && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-0.5">
                        <Star className="w-2.5 h-2.5 fill-amber-600 text-amber-600" />
                        <span>VIP</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Profile Main Info */}
                <div className="flex items-center gap-3">
                  {item._isInternal ? (
                    <Avatar config={item.AvatarConfig} size={44} />
                  ) : (
                    <div className="w-11 h-11 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-700 font-black flex items-center justify-center text-base flex-shrink-0 shadow-2xs">
                      {item.Name?.slice(0, 1) || '고'}
                    </div>
                  )}

                  <div className="overflow-hidden flex-1">
                    <h3 className="font-extrabold text-sm text-slate-900 truncate group-hover:text-indigo-600 transition-colors">
                      {item.Name}
                    </h3>
                    <p className="text-xs text-slate-500 truncate mt-0.5 font-medium">
                      {item._isInternal 
                        ? `${item.Department || '부서미지정'} • ${item.JobTitle || item.Position || '사원'}` 
                        : (item.Company || '개인 고객')}
                    </p>
                  </div>
                </div>

                {/* Contact Coordinates */}
                <div className="space-y-1.5 pt-2 border-t border-slate-100 text-xs">
                  <div className="flex items-center gap-2 text-slate-600 truncate">
                    <Mail className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="font-mono text-[11px] truncate text-indigo-600 font-semibold">{item.Email}</span>
                  </div>

                  {item.Phone && (
                    <div className="flex items-center gap-2 text-slate-600 truncate">
                      <Phone className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="font-mono text-[11px] truncate">{item.Phone}</span>
                    </div>
                  )}
                </div>

                {/* Card Bottom Quick Actions */}
                <div className="pt-2 flex items-center justify-between gap-1 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailModalContact(item);
                    }}
                    className="text-[11px] text-slate-500 hover:text-slate-800 font-bold px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                  >
                    상세보기 ❯
                  </button>

                  <div className="flex items-center gap-1">
                    {item.Phone && (
                      <a
                        href={`tel:${item.Phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                        title="전화 걸기"
                      >
                        <PhoneCall className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleComposeClick(item.Email);
                      }}
                      className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors cursor-pointer"
                      title="메일 작성"
                    >
                      <Mail className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* B. List / Table View */
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase">
                  <th className="py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={selectedEmailSet.size === filteredList.length && filteredList.length > 0}
                      onChange={handleSelectAllInView}
                      className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                    />
                  </th>
                  <th className="py-3 px-4">성명</th>
                  <th className="py-3 px-4">소속 / 부서</th>
                  <th className="py-3 px-4">직급 / 직책</th>
                  <th className="py-3 px-4">이메일 주소</th>
                  <th className="py-3 px-4">연락처</th>
                  <th className="py-3 px-4">구분 / 공개</th>
                  <th className="py-3 px-4 text-right">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredList.map(item => {
                  const itemKey = item.UserID || item.CustomerID;
                  const isChecked = selectedEmailSet.has(item.Email);
                  const isPrivate = item.Visibility === 'PRIVATE';

                  return (
                    <tr
                      key={itemKey}
                      onClick={() => setDetailModalContact(item)}
                      className="hover:bg-indigo-50/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelectEmail(item.Email)}
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 cursor-pointer"
                        />
                      </td>
                      <td className="py-3 px-4 font-bold text-slate-900">
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold text-sm">{item.Name}</span>
                          {item.Category === 'VIP' && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700 font-medium">
                        {item._isInternal ? item.Department : (item.Company || '-')}
                      </td>
                      <td className="py-3 px-4 text-slate-600">
                        {item._isInternal ? (item.JobTitle || item.Position || '사원') : (item.Category || '고객')}
                      </td>
                      <td className="py-3 px-4 font-mono font-semibold text-indigo-600">
                        {item.Email}
                      </td>
                      <td className="py-3 px-4 font-mono text-slate-600">
                        {item.Phone || '-'}
                      </td>
                      <td className="py-3 px-4">
                        {item._isInternal ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
                            사내직원
                          </span>
                        ) : isPrivate ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            🔒 나만보기
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-100">
                            🌐 전사공유
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleComposeClick(item.Email)}
                          className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-bold text-xs inline-flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          <Mail className="w-3 h-3" />
                          <span>메일</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 4. Contact Detail Popup Modal (팝업 창) */}
      {/* ========================================================================= */}
      {detailModalContact && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-lg w-full p-6 text-left space-y-5 animate-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-start justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3.5">
                {detailModalContact._isInternal ? (
                  <Avatar config={detailModalContact.AvatarConfig} size={56} />
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white font-black flex items-center justify-center text-xl shadow-md shadow-indigo-100 flex-shrink-0">
                    {detailModalContact.Name?.slice(0, 1) || '고'}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-black text-slate-900 tracking-tight">{detailModalContact.Name}</h3>
                    {detailModalContact.Category === 'VIP' && (
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 flex items-center gap-1">
                        <Star className="w-3 h-3 fill-amber-600 text-amber-600" />
                        <span>VIP</span>
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-bold text-indigo-600 mt-0.5">
                    {detailModalContact._isInternal 
                      ? `${detailModalContact.Department || '부서 미정'} • ${detailModalContact.JobTitle || detailModalContact.Position || '사원'}` 
                      : (detailModalContact.Company || '외부 고객')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setDetailModalContact(null)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Key Information Cards */}
            <div className="space-y-3">
              {/* Email Card */}
              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div className="overflow-hidden">
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">이메일 주소</span>
                    <span className="font-mono font-bold text-slate-900 truncate block">{detailModalContact.Email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleCopyText(detailModalContact.Email, 'email')}
                    className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                    title="이메일 복사"
                  >
                    {copiedField === 'email' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDetailModalContact(null);
                      handleComposeClick(detailModalContact.Email);
                    }}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-2xs cursor-pointer flex items-center gap-1"
                  >
                    <Send className="w-3 h-3" />
                    <span>메일 쓰기</span>
                  </button>
                </div>
              </div>

              {/* Phone Card */}
              {detailModalContact.Phone && (
                <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden">
                      <span className="text-[10px] font-bold text-slate-400 uppercase block">연락처</span>
                      <span className="font-mono font-bold text-slate-900 truncate block">{detailModalContact.Phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleCopyText(detailModalContact.Phone, 'phone')}
                      className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-white rounded-lg border border-transparent hover:border-slate-200 transition-all cursor-pointer"
                      title="전화번호 복사"
                    >
                      {copiedField === 'phone' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <a
                      href={`tel:${detailModalContact.Phone}`}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-2xs cursor-pointer flex items-center gap-1"
                    >
                      <PhoneCall className="w-3 h-3" />
                      <span>전화 걸기</span>
                    </a>
                  </div>
                </div>
              )}

              {/* Company / Department Card */}
              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-slate-200 text-slate-700 flex items-center justify-center flex-shrink-0">
                    <Building className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase block">소속 및 부서 / 구분</span>
                    <span className="font-bold text-slate-800 block">
                      {detailModalContact._isInternal 
                        ? `${detailModalContact.Department || '부서 미정'} (사내 계정)` 
                        : (detailModalContact.Company || '외부 거래처')}
                    </span>
                  </div>
                </div>

                {!detailModalContact._isInternal && (
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    detailModalContact.Visibility === 'PRIVATE' 
                      ? 'bg-amber-50 text-amber-700 border-amber-200' 
                      : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  }`}>
                    {detailModalContact.Visibility === 'PRIVATE' ? '🔒 나만 보기' : '🌐 전사 공유'}
                  </span>
                )}
              </div>

              {/* Memo Note Card */}
              {detailModalContact.Memo && (
                <div className="p-3.5 bg-indigo-50/50 border border-indigo-100 rounded-2xl text-xs space-y-1">
                  <span className="text-[10px] font-bold text-indigo-900 uppercase block">메모 / 참고사항</span>
                  <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{detailModalContact.Memo}</p>
                </div>
              )}

              {/* Created By & Date for external customers */}
              {!detailModalContact._isInternal && (
                <div className="text-[11px] text-slate-400 pt-1 flex items-center justify-between">
                  <span>등록자: <strong>{detailModalContact.CreatedBy || '본인'}</strong></span>
                  {detailModalContact.CreatedAt && (
                    <span>등록일: {new Date(detailModalContact.CreatedAt).toLocaleDateString('ko-KR')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Modal Bottom Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div>
                {!detailModalContact._isInternal && (
                  <button
                    type="button"
                    onClick={() => handleDeleteCustomer(detailModalContact)}
                    className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>삭제</span>
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {!detailModalContact._isInternal && (
                  <button
                    type="button"
                    onClick={() => handleOpenEditModal(detailModalContact)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs rounded-xl cursor-pointer transition-colors flex items-center gap-1"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>정보 수정</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setDetailModalContact(null)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-xs rounded-xl cursor-pointer transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* 5. Contact Add / Edit Form Popup Modal */}
      {/* ========================================================================= */}
      {isFormModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-lg w-full p-6 md:p-8 text-left space-y-5 animate-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                  {editingCustomer ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {editingCustomer ? '고객 / 연락처 정보 수정' : '새 연락처 / 고객 등록'}
                  </h3>
                  <span className="text-[11px] text-slate-400 font-medium">
                    공개 범위를 설정하여 나만 보거나 전사와 공유할 수 있습니다.
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsFormModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-xl hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCustomerSubmit} className="space-y-4">
              
              {/* Visibility Setting (나만 보기 vs 전사 공유) */}
              <div className="space-y-1.5 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl">
                <label className="text-xs font-bold text-slate-700 uppercase block">공개 범위 설정 *</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setCustVisibility('PUBLIC')}
                    className={`p-2.5 rounded-xl border text-xs font-bold flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                      custVisibility === 'PUBLIC'
                        ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      <span>전사 공유</span>
                    </div>
                    <span className={`text-[10px] font-normal ${custVisibility === 'PUBLIC' ? 'text-indigo-100' : 'text-slate-400'}`}>
                      모든 임직원 열람 가능
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCustVisibility('PRIVATE')}
                    className={`p-2.5 rounded-xl border text-xs font-bold flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                      custVisibility === 'PRIVATE'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-sm'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-1">
                      <Lock className="w-3.5 h-3.5" />
                      <span>나만 보기 (비공개)</span>
                    </div>
                    <span className={`text-[10px] font-normal ${custVisibility === 'PRIVATE' ? 'text-amber-100' : 'text-slate-400'}`}>
                      내 계정에서만 열람
                    </span>
                  </button>
                </div>
              </div>

              {/* Category Select */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600 uppercase block">분류 카테고리</label>
                <select
                  value={custCategory}
                  onChange={e => setCustCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-bold cursor-pointer"
                >
                  {tabs.filter(t => t.id !== 'INTERNAL' && t.id !== 'ALL_CUSTOMERS' && t.id !== 'PRIVATE').map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              {/* Name & Email */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 uppercase block">성명 *</label>
                  <input
                    type="text"
                    placeholder="성명 입력"
                    value={custName}
                    onChange={e => setCustName(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 uppercase block">이메일 주소 *</label>
                  <input
                    type="email"
                    placeholder="customer@example.com"
                    value={custEmail}
                    onChange={e => setCustEmail(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                    required
                  />
                </div>
              </div>

              {/* Phone & Company */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 uppercase block">연락처</label>
                  <input
                    type="text"
                    placeholder="010-1234-5678"
                    value={custPhone}
                    onChange={e => setCustPhone(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-600 uppercase block">소속 회사 / 기관</label>
                  <input
                    type="text"
                    placeholder="예: 알파테크 주식회사"
                    value={custCompany}
                    onChange={e => setCustCompany(e.target.value)}
                    className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                  />
                </div>
              </div>

              {/* Memo */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-600 uppercase block">메모 / 특이사항</label>
                <textarea
                  rows={3}
                  placeholder="고객과의 주요 논의 내용, 미팅 히스토리, 비고 사항 등을 자유롭게 입력하세요."
                  value={custMemo}
                  onChange={e => setCustMemo(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs sm:text-sm text-slate-900 focus:outline-none focus:border-indigo-600 font-medium resize-none"
                />
              </div>

              {/* Form Buttons */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsFormModalOpen(false)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl cursor-pointer transition-colors"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm cursor-pointer transition-all active:scale-95"
                >
                  {editingCustomer ? '수정 완료' : '등록하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AddressBookTab;
